// client/src/utils/escrowPrint.js — the escrow records on paper, in the examined format.
//
// The server (routes/escrowExamReports.js) returns the data in the column order IDFPR
// accepted from this office in its January 2018 examination; this lays it out for
// printing. Two reasons it matters beyond tidiness: the examination asks for a HARD
// COPY of the journal kept at the office for 60 days (Part VIII-5), and an examiner
// reads the ledger card and worksheet as documents, not as screens.
//
// Every value is escaped — payee names and memos are typed by people.

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const money = (v) => (v === null || v === undefined || v === ''
    ? ''
    : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));

const STYLE = `
    body { font-family: Georgia, 'Times New Roman', serif; color: #111; margin: 0; }
    h1 { font-size: 18px; margin: 0 0 2px; text-transform: uppercase; letter-spacing: .04em; }
    .sub { font-size: 12px; color: #333; margin-bottom: 14px; }
    table { width: 100%; border-collapse: collapse; font-size: 11px; }
    th, td { border: 1px solid #999; padding: 4px 6px; vertical-align: top; }
    th { background: #eee; text-align: left; }
    td.n, th.n { text-align: right; white-space: nowrap; }
    .box { border: 1px solid #999; padding: 8px 10px; margin: 10px 0; font-size: 12px; }
    .row { display: flex; justify-content: space-between; padding: 2px 0; }
    .row.total { border-top: 1px solid #111; font-weight: bold; margin-top: 4px; padding-top: 4px; }
    .auth { font-size: 10px; color: #444; margin-top: 16px; }
    .sign { margin-top: 36px; font-size: 12px; display: flex; gap: 48px; }
    .sign div { border-top: 1px solid #111; padding-top: 4px; min-width: 220px; }
    @page { margin: 0.6in; }
    @media print { .noprint { display: none; } }
`;

const open = (title, body) => {
    const w = window.open('', '_blank');
    if (!w) {
        alert('The print window was blocked. Allow pop-ups for this site and try again.');
        return;
    }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
        <style>${STYLE}</style></head><body>${body}
        <p class="auth">Printed ${esc(new Date().toLocaleString())} from the BREC virtual office.</p>
        </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 300);
};

/** The journal — 1450.750(i)(1). `r` is GET /api/escrow/journal-report. */
export const printJournal = (r) => {
    const rows = (r.lines || []).map((l) => `<tr>
        <td>${esc(l.id_number)}</td><td>${esc(l.date)}</td><td>${esc(l.received_from_or_payee)}</td>
        <td>${esc(l.property_address)}</td><td>${esc(l.seller_name)}</td><td>${esc(l.buyer_name)}</td>
        <td class="n">${money(l.amount_received)}</td><td class="n">${money(l.amount_disbursed)}</td>
        <td>${esc(l.check_number)}</td><td class="n">${money(l.running_balance)}</td></tr>`).join('');
    open(`Escrow Journal ${r.period?.from || ''} to ${r.period?.to || ''}`, `
        <h1>${esc(r.title)} — ${esc(r.brokerage)}</h1>
        <div class="sub">${esc(r.account?.name)} · ${esc(r.account?.bank)} · Account ${esc(r.account?.number)}<br>
            Period ${esc(r.period?.from)} to ${esc(r.period?.to)} · Opening balance ${money(r.opening_balance)}</div>
        <table><thead><tr>${(r.columns || []).map((c, i) =>
            `<th${i >= 6 && i !== 8 ? ' class="n"' : ''}>${esc(c)}</th>`).join('')}</tr></thead>
        <tbody>${rows || '<tr><td colspan="10">NO TRANSACTIONS IN THIS PERIOD</td></tr>'}</tbody></table>
        <div class="box"><div class="row total"><span>Closing balance</span><span>${money(r.closing_balance)}</span></div></div>
        <p class="auth">${esc(r.authority)}</p>`);
};

/** The ledger card — 1450.750(i)(2). `r` is GET /api/escrow/ledgers/:id/card. */
export const printLedgerCard = (r) => {
    const rows = (r.lines || []).map((l) => `<tr><td>${esc(l.date)}</td><td>${esc(l.description)}</td>
        <td>${esc(l.check_number)}</td><td class="n">${money(l.amount)}</td><td class="n">${money(l.balance)}</td></tr>`).join('');
    const parties = (r.all_parties || []).map((p) => `${esc(p.name)} (${esc(p.role)})`).join('; ');
    open(`${r.title} ${r.transaction_reference || ''}`, `
        <h1>${esc(r.title)} — ${esc(r.brokerage)}</h1>
        <div class="sub">${esc(r.account?.name)} · ${esc(r.account?.bank)}</div>
        <div class="box">
            <div class="row"><span>Reference</span><span>${esc(r.transaction_reference)}</span></div>
            <div class="row"><span>Property</span><span>${esc(r.property_address || '—')}</span></div>
            <div class="row"><span>Seller / Landlord / Owner</span><span>${esc(r.seller || '—')}</span></div>
            <div class="row"><span>Buyer / Tenant</span><span>${esc(r.buyer || '—')}</span></div>
            <div class="row"><span>All parties</span><span>${parties || '—'}</span></div>
            <div class="row"><span>Funds</span><span>${esc(r.funds_type)} · ${esc(r.status)}</span></div>
            <div class="row"><span>Opened / closed</span><span>${esc(r.opened_on || '')} / ${esc(r.closed_on || '—')}</span></div>
        </div>
        <table><thead><tr><th>Date</th><th>Description</th><th>Check #</th><th class="n">Amount</th><th class="n">Balance</th></tr></thead>
        <tbody>${rows || `<tr><td colspan="5">${esc(r.empty_note || 'NO TRANSACTIONS')}</td></tr>`}</tbody></table>
        <div class="box"><div class="row total"><span>Balance</span><span>${money(r.balance)}</span></div></div>
        <p class="auth">${esc(r.authority)} — a ledger for each transaction, showing the names of all parties.</p>`);
};

/** The reconciliation worksheet — 1450.750(i)(3). `r` is GET /api/escrow/reconciliation-worksheet/:id. */
export const printWorksheet = (r) => {
    const b = r.bank_section || {}, j = r.journal_section || {}, l = r.ledger_section || {};
    const items = (list) => (list || []).map((i) => `<tr><td>${esc(i.item_date)}</td><td>${esc(i.payee_or_source)}</td>
        <td>${esc(i.check_number)}</td><td class="n">${money(i.amount)}</td></tr>`).join('')
        || '<tr><td colspan="4">None</td></tr>';
    open(`Reconciliation ${r.month || ''}`, `
        <h1>${esc(r.title)} — ${esc(r.brokerage)}</h1>
        <div class="sub">${esc(r.bank)} · Account ${esc(r.account_number)} · Statement dated ${esc(r.month)}</div>
        <div class="box"><strong>Bank</strong>
            <div class="row"><span>Balance per bank statement</span><span>${money(b.balance_per_statement)}</span></div>
            <div class="row"><span>Add: deposits not credited</span><span>${money(b.add_deposits_not_credited)}</span></div>
            <div class="row"><span>Less: outstanding checks</span><span>(${money(b.less_outstanding_checks)})</span></div>
            <div class="row total"><span>Reconciled bank balance</span><span>${money(b.reconciled_bank_balance)}</span></div></div>
        <div class="box"><strong>Journal</strong>
            <div class="row total"><span>Balance per journal</span><span>${money(j.balance_brought_forward)}</span></div></div>
        <div class="box"><strong>Ledgers</strong>
            <div class="row"><span>Total of open transactions</span><span>${money(l.total_of_open_transactions)}</span></div>
            <div class="row"><span>Broker funds</span><span>${money(l.broker_funds)}</span></div>
            <div class="row total"><span>Reconciled ledger balance</span><span>${money(l.reconciled_ledger_balance)}</span></div></div>
        <div class="box"><div class="row total"><span>Status</span><span>${esc(r.status)} — difference ${money(r.difference)}</span></div></div>
        <h1 style="font-size:14px;margin-top:18px">Outstanding checks</h1>
        <table><thead><tr><th>Date</th><th>Payee</th><th>Check #</th><th class="n">Amount</th></tr></thead><tbody>${items(r.outstanding_checks)}</tbody></table>
        <h1 style="font-size:14px;margin-top:18px">Deposits in transit</h1>
        <table><thead><tr><th>Date</th><th>Source</th><th>Ref</th><th class="n">Amount</th></tr></thead><tbody>${items(r.deposits_in_transit)}</tbody></table>
        ${r.discrepancy_explanation ? `<div class="box"><strong>Explanation of the difference</strong><p>${esc(r.discrepancy_explanation)}</p></div>` : ''}
        ${r.corrective_action ? `<div class="box"><strong>Corrective action taken</strong><p>${esc(r.corrective_action)}</p></div>` : ''}
        ${r.broker_signed_at ? `<p style="font-size:12px">Reviewed, signed and dated by the broker in the portal on ${esc(new Date(r.broker_signed_at).toLocaleString())}.</p>` : ''}
        <div class="sign"><div>Prepared by / date</div><div>Managing broker / date</div></div>
        <p class="auth">${esc(r.authority)}</p>`);
};
